import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, FileCheck2, FileSignature, Info, Lock,
  RefreshCw, ShieldCheck, Sparkles, Upload, UploadCloud, X,
} from "lucide-react";
import { api } from "../lib/api";

const REGIMES = [
  { code: "612", label: "612 · Persona Física con Actividades Empresariales y Profesionales" },
  { code: "605", label: "605 · Sueldos y Salarios e Ingresos Asimilados" },
  { code: "606", label: "606 · Arrendamiento" },
  { code: "621", label: "621 · Incorporación Fiscal" },
  { code: "625", label: "625 · RESICO Personas Físicas" },
  { code: "626", label: "626 · RESICO Personas Morales" },
  { code: "601", label: "601 · General de Ley Personas Morales" },
];

const STATUS_META = {
  active: { tone: "ok", label: "Listo para facturar", desc: "Puedes emitir facturas bajo tu RFC." },
  pending_validation: { tone: "warn", label: "Configuración pendiente", desc: "Completa tu Certificado de Sello Digital para comenzar a facturar." },
  not_configured: { tone: "muted", label: "Todavía no puedes facturar", desc: "Registra tu RFC y régimen fiscal para empezar." },
  error: { tone: "bad", label: "Revisa tu configuración", desc: "Existe un problema con la configuración." },
};

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function CertDropzone({ label, hint, accept, inputRef, file, onFile, onClear, testId, extPreview }) {
  const [dragging, setDragging] = useState(false);
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragging(false);
    const dropped = e.dataTransfer?.files?.[0];
    if (dropped) {
      onFile(dropped);
      try {
        const dt = new DataTransfer();
        dt.items.add(dropped);
        if (inputRef.current) inputRef.current.files = dt.files;
      } catch { /* Safari/old browsers */ }
    }
  };
  const filled = !!file;
  return (
    <label
      className={`cert-dropzone${dragging ? " dragging" : ""}${filled ? " filled" : ""}`}
      onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      data-testid={`${testId}-dropzone`}
    >
      <span className="cert-dz-label">{label}</span>
      <span className="cert-dz-body">
        {filled
          ? <FileCheck2 size={26} color="#059669" />
          : <UploadCloud size={26} color={dragging ? "#059669" : "#94a3b8"} />}
        <span className="cert-dz-text">
          <b>{filled ? file.name : (extPreview || "Arrastra o haz clic")}</b>
          <small>{filled ? formatBytes(file.size) : (hint || ".cer o .key — máx 2 MB")}</small>
        </span>
      </span>
      {filled && (
        <button
          type="button"
          className="cert-dz-clear"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClear(); }}
          aria-label="Quitar archivo"
          data-testid={`${testId}-clear`}
        >
          <X size={11} /> Quitar
        </button>
      )}
      <input
        type="file"
        accept={accept}
        ref={inputRef}
        onChange={(e) => onFile(e.target.files?.[0] || null)}
        data-testid={testId}
      />
    </label>
  );
}

function CsdUploader({ businessId, invoicing, onUploaded, onError }) {
  const cerRef = useRef(null);
  const keyRef = useRef(null);
  const [cer, setCer] = useState(null);
  const [keyFile, setKeyFile] = useState(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [okMsg, setOkMsg] = useState("");

  const submit = async () => {
    if (!cer || !keyFile || !password) return;
    setBusy(true); setOkMsg("");
    const form = new FormData();
    form.append("cer", cer);
    form.append("key", keyFile);
    form.append("password", password);
    try {
      const { data } = await api.post(`/businesses/${businessId}/csd`, form, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      onUploaded && onUploaded(data.invoicing);
      setOkMsg("CSD cifrado y guardado ✓");
      setCer(null); setKeyFile(null); setPassword("");
      if (cerRef.current) cerRef.current.value = "";
      if (keyRef.current) keyRef.current.value = "";
    } catch (issue) {
      const d = issue.response?.data?.detail;
      onError && onError(typeof d === "string" ? d : d?.message || "No pudimos guardar el CSD.");
    } finally { setBusy(false); }
  };

  const provision = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/facturapi/provision`, {}, { withCredentials: true });
      onUploaded && onUploaded(data.invoicing);
      setOkMsg("Facturapi provisionado correctamente ✓");
    } catch (issue) {
      const d = issue.response?.data?.detail;
      onError && onError(typeof d === "string" ? d : d?.detail?.message || d?.detail || "No pudimos activar Facturapi.");
    } finally { setBusy(false); }
  };

  const uploaded = invoicing?.csd_status === "valid";
  const active = invoicing?.status === "active";

  return (
    <>
      <div className="billing-csd-locked" style={{ borderLeftColor: uploaded ? "#059669" : "#28a878" }} data-testid="billing-csd-block">
        <Lock size={22} style={{ color: uploaded ? "#059669" : "#28a878" }} />
        <div>
          <b>{uploaded ? "CSD activo ✓" : "Sube tu Certificado de Sello Digital"}</b>
          <p>Los archivos <code>.cer</code>, <code>.key</code> y tu contraseña se <b>cifran con AES-128</b> antes de guardarse. Nadie más que tú puede leerlos.</p>
          <small>🔒 Tus certificados se usan solo para emitir facturas bajo tu propio RFC.</small>
        </div>
      </div>
      <div className="billing-csd-fields" data-testid="billing-csd-fields">
        <div className="cert-dropzones-row">
          <CertDropzone
            label="ARCHIVO .CER"
            hint="Certificado del SAT · máx 2 MB"
            accept=".cer,application/octet-stream"
            inputRef={cerRef}
            file={cer}
            onFile={setCer}
            onClear={() => { setCer(null); if (cerRef.current) cerRef.current.value = ""; }}
            testId="billing-cer-file"
          />
          <CertDropzone
            label="ARCHIVO .KEY"
            hint="Llave privada · máx 2 MB"
            accept=".key,application/octet-stream"
            inputRef={keyRef}
            file={keyFile}
            onFile={setKeyFile}
            onClear={() => { setKeyFile(null); if (keyRef.current) keyRef.current.value = ""; }}
            testId="billing-key-file"
          />
        </div>
        <label className="cert-password-field">
          <span>Contraseña del certificado</span>
          <input type="password" placeholder="•••••••••" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="billing-csd-password" />
        </label>
      </div>
      <div className="billing-actions" style={{ gap: 10 }}>
        <button type="button" className="cash-btn primary" onClick={submit} disabled={busy || !cer || !keyFile || !password} data-testid="billing-csd-submit">
          <Upload size={13} /> {busy ? "Cifrando..." : uploaded ? "Reemplazar CSD" : "Subir CSD cifrado"}
        </button>
        {uploaded && !active && (
          <button type="button" className="cash-btn ghost" onClick={provision} disabled={busy} data-testid="billing-facturapi-provision">
            <ShieldCheck size={13} /> Activar Facturapi
          </button>
        )}
      </div>
      {okMsg && <p className="expenses-notice" data-testid="billing-csd-ok">{okMsg}</p>}
    </>
  );
}

function EfirmaUploader({ businessId, onError, onUploaded }) {
  const cerRef = useRef(null);
  const keyRef = useRef(null);
  const [cer, setCer] = useState(null);
  const [keyFile, setKeyFile] = useState(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [okMsg, setOkMsg] = useState("");
  const submit = async () => {
    if (!cer || !keyFile || !password) return;
    setBusy(true); setOkMsg("");
    const form = new FormData();
    form.append("cer", cer); form.append("key", keyFile); form.append("password", password);
    try {
      const { data } = await api.post(`/businesses/${businessId}/efirma`, form, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      setOkMsg(`e.firma cifrada y guardada ✓${data.rfc ? ` · RFC ${data.rfc}` : ""}`);
      setCer(null); setKeyFile(null); setPassword("");
      if (cerRef.current) cerRef.current.value = "";
      if (keyRef.current) keyRef.current.value = "";
      // Refresh parent's manifest status so the "Firmar" button enables.
      if (onUploaded) onUploaded();
    } catch (issue) {
      const d = issue.response?.data?.detail;
      onError && onError(typeof d === "string" ? d : d?.message || "No pudimos guardar la e.firma.");
    } finally { setBusy(false); }
  };
  return (
    <div style={{ marginTop: 14 }} data-testid="efirma-uploader">
      <b style={{ display: "block", marginBottom: 8, fontSize: 13, color: "#374151" }}>Opcional — guarda tu e.firma cifrada (respaldo local)</b>
      <div className="billing-csd-fields">
        <div className="cert-dropzones-row">
          <CertDropzone
            label="E.FIRMA .CER"
            hint="Certificado e.firma · máx 2 MB"
            accept=".cer,application/octet-stream"
            inputRef={cerRef}
            file={cer}
            onFile={setCer}
            onClear={() => { setCer(null); if (cerRef.current) cerRef.current.value = ""; }}
            testId="efirma-cer-file"
          />
          <CertDropzone
            label="E.FIRMA .KEY"
            hint="Llave privada e.firma · máx 2 MB"
            accept=".key,application/octet-stream"
            inputRef={keyRef}
            file={keyFile}
            onFile={setKeyFile}
            onClear={() => { setKeyFile(null); if (keyRef.current) keyRef.current.value = ""; }}
            testId="efirma-key-file"
          />
        </div>
        <label className="cert-password-field">
          <span>Contraseña e.firma</span>
          <input type="password" placeholder="•••••••••" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="efirma-password" />
        </label>
      </div>
      <div className="billing-actions" style={{ marginTop: 10 }}>
        <button type="button" className="cash-btn ghost" onClick={submit} disabled={busy || !cer || !keyFile || !password} data-testid="efirma-submit">
          <Upload size={13} /> {busy ? "Cifrando..." : "Guardar e.firma cifrada"}
        </button>
      </div>
      {okMsg && <p className="expenses-notice" data-testid="efirma-ok" style={{ marginTop: 8 }}>{okMsg}</p>}
    </div>
  );
}

const MANIFIESTO_URL = "https://www.facturapi.io/embedded/manifiesto";

// Human copy for backend `error_code` values so the UI is precise instead of
// hiding real Facturapi problems behind an endless spinner.
const ERROR_COPY = {
  RFC_MISSING: "Registra tu RFC en el paso 1 antes de continuar.",
  PROFILE_INCOMPLETE: "Completa nombre, régimen y código postal antes de sincronizar.",
  FACTURAPI_KEY_MISSING: "El servidor no tiene configurada la API Key de Facturapi. Contáctanos.",
  FACTURAPI_KEY_WRONG_ROLE: "La API Key de Facturapi no tiene permisos para dar de alta organizaciones.",
  RFC_MISMATCH: "El RFC del certificado no coincide con el RFC registrado.",
  CSD_INVALID: "Tu CSD no es válido. Vuelve a subirlo desde el paso 2.",
  CSD_EXPIRED: "Tu CSD está vencido. Genera uno nuevo en el SAT y súbelo de nuevo.",
  ORGANIZATION_NOT_FOUND: "No encontramos tu organización en Facturapi. Intenta reintentar.",
  ORGANIZATION_CREATE_FAILED: "No pudimos crear tu organización en Facturapi. Revisa que los datos fiscales sean correctos e intenta de nuevo.",
  FACTURAPI_UNAVAILABLE: "Facturapi está tardando en responder. Espera unos segundos y vuelve a intentar.",
};

function ManifestStep({ businessId, onSigned, onError }) {
  const [status, setStatus] = useState(null);
  const [polling, setPolling] = useState(false);
  const [checkError, setCheckError] = useState("");
  const check = async () => {
    try {
      const { data } = await api.get(`/businesses/${businessId}/manifest/status`, { withCredentials: true });
      setStatus(data);
      setCheckError("");
      if (data.status === "signed" && onSigned) onSigned();
      // Backend told us the operation failed — stop polling and surface the real error.
      if (data.status === "error") {
        setPolling(false);
        const human = ERROR_COPY[data.error_code] || data.error_message || "Facturapi reportó un error.";
        setCheckError(human);
        onError && onError(human);
      }
      return data;
    } catch (issue) {
      setPolling(false);
      const detail = issue.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "No pudimos verificar tu Carta Manifiesto.";
      setCheckError(msg);
      onError && onError(msg);
    }
  };
  useEffect(() => { check(); /* eslint-disable-next-line */ }, [businessId]);
  useEffect(() => {
    if (!polling) return;
    const id = setInterval(() => {
      check().then((d) => { if (!d || d?.status === "signed" || d?.status === "error") setPolling(false); });
    }, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [polling]);
  const readyToSign = !!status?.ready_to_sign;
  const efirmaValid = status?.efirma_status === "valid" || status?.status === "signed";
  const openManifest = async () => {
    if (!readyToSign) {
      onError && onError("Sube tu e.firma cifrada antes de firmar la Carta Manifiesto.");
      return;
    }
    await api.post(`/businesses/${businessId}/manifest/status`, { status: "in_progress" }, { withCredentials: true });
    window.open(MANIFIESTO_URL, "_blank", "noopener,noreferrer");
    setPolling(true);
  };
  return (
    <section className="billing-card" data-testid="billing-step3-manifest">
      <header><h2>Firma tu Carta Manifiesto</h2><p>Este último paso autoriza al PAC (Facturapi) a timbrar CFDI bajo tu RFC. La firma se realiza con tu <b>e.firma</b> del mismo RFC del CSD.</p></header>
      <ul className="billing-review" data-testid="billing-manifest-checklist">
        <li><span>Datos fiscales</span><b>✓ Configurados</b></li>
        <li><span>CSD (para timbrar facturas)</span><b data-testid="manifest-csd-check">{status?.csd_status === "valid" ? "✓ Validado" : "○ Opcional para firmar"}</b></li>
        <li><span>e.firma cifrada</span><b data-testid="manifest-efirma-check">{efirmaValid ? "✓ Válida — habilita la firma" : "○ Súbela abajo para firmar"}</b></li>
        <li><span>Carta Manifiesto</span><b data-testid="manifest-current-status">{{
          pending: "○ Pendiente de firma",
          in_progress: "⏳ En proceso",
          ready: "⏳ Facturapi lista — falta firma",
          signed: "✓ Firmada",
          error: "⚠ Error al firmar",
        }[status?.status || "pending"]}</b></li>
      </ul>
      {status?.status === "error" && (
        <div className="billing-csd-locked" style={{ borderLeftColor: "#dc2626", background: "#fef2f2" }} data-testid="manifest-error-banner">
          <Info size={22} style={{ color: "#dc2626" }} />
          <div>
            <b>No pudimos avanzar tu configuración en Facturapi</b>
            <p data-testid="manifest-error-message">{ERROR_COPY[status.error_code] || status.error_message}</p>
            {status.error_code && <small data-testid="manifest-error-code">Código: <code>{status.error_code}</code>{status.source ? ` · ${status.source}` : ""}</small>}
            <div style={{ marginTop: 10 }}>
              <button type="button" className="cash-btn ghost" onClick={check} data-testid="manifest-error-retry">
                <RefreshCw size={13} /> Reintentar
              </button>
            </div>
          </div>
        </div>
      )}
      {status?.status === "signed" ? (
        <div className="billing-csd-locked" style={{ borderLeftColor: "#059669" }} data-testid="manifest-signed-banner">
          <ShieldCheck size={22} style={{ color: "#059669" }} />
          <div>
            <b>Carta Manifiesto firmada</b>
            <p>{status.signed_at ? `Firmada el ${status.signed_at.slice(0, 10)}.` : "Firmada correctamente."} Ya puedes emitir facturas.</p>
          </div>
        </div>
      ) : status?.status === "error" ? null : (
        <>
          <div className="billing-csd-locked" style={{ borderLeftColor: readyToSign ? "#28a878" : "#f59e0b" }} data-testid="manifest-cta-banner">
            <Lock size={22} style={{ color: readyToSign ? "#28a878" : "#f59e0b" }} />
            <div>
              <b>{readyToSign ? "Listo para firmar tu Carta Manifiesto" : "Sube tu e.firma para habilitar la firma"}</b>
              <p>
                {readyToSign
                  ? <>Al hacer clic abriremos <code>facturapi.io/embedded/manifiesto</code> en una pestaña nueva. La firma se realiza con la <b>e.firma</b> del mismo RFC.</>
                  : <>Necesitas tu <code>.cer</code>, <code>.key</code> y contraseña de <b>e.firma</b> (no CSD) del mismo RFC. Súbelos en la sección <b>"e.firma cifrada"</b> de abajo — se cifran con AES-128.</>}
              </p>
              <small>🔒 Mi Contador nunca ve tu e.firma sin cifrar — el módulo oficial de Facturapi procesa la firma. El CSD es opcional aquí; sirve solo para timbrar facturas.</small>
            </div>
          </div>
          <EfirmaUploader businessId={businessId} onError={onError} onUploaded={check} />
        </>
      )}
      <div className="billing-actions">
        <button type="button" className="cash-btn ghost" onClick={() => window.scrollTo(0, 0)} data-testid="billing-step3-back">Atrás</button>
        {status?.status !== "signed" ? (
          <>
            <button type="button" className="cash-btn primary" onClick={openManifest} disabled={!readyToSign} data-testid="billing-manifest-open" title={readyToSign ? "Abrir Carta Manifiesto en Facturapi" : "Sube tu CSD y e.firma primero"}>
              <ShieldCheck size={14} /> Firmar Carta Manifiesto
            </button>
            <button type="button" className="cash-btn ghost" onClick={check} data-testid="billing-manifest-refresh">
              Ya firmé, verificar
            </button>
          </>
        ) : (
          <button type="button" className="cash-btn primary" onClick={onSigned} data-testid="billing-manifest-continue">
            Continuar <ArrowRight size={14} />
          </button>
        )}
      </div>
      {polling && status?.status !== "error" && (
        <p style={{ marginTop: 12, color: "#059669", fontSize: 12 }} data-testid="manifest-polling">
          ⏳ Esperando tu firma... verificamos cada 15 segundos.
        </p>
      )}
      {checkError && status?.status !== "error" && (
        <p style={{ marginTop: 12, color: "#dc2626", fontSize: 12 }} data-testid="manifest-check-error">{checkError}</p>
      )}
    </section>
  );
}

export default function FiscalSetupTab() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [businesses, setBusinesses] = useState([]);
  const [businessId, setBusinessId] = useState(null);
  const [tp, setTp] = useState({ rfc: "", legal_name: "", tax_regime: "", fiscal_postal_code: "", email: "" });
  const [invoicing, setInvoicing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [reconciling, setReconciling] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);
  const autoSyncedRef = useRef(false);

  const active = useMemo(() => businesses.find((b) => b.is_active), [businesses]);

  const loadBusinesses = useCallback(async () => {
    try {
      const { data } = await api.get("/businesses", { withCredentials: true });
      setBusinesses(data || []);
      const bid = (data || []).find((b) => b.is_active)?.id;
      if (bid) setBusinessId(bid);
    } catch (issue) { setError(issue.response?.data?.detail || "No pudimos cargar tus empresas."); }
  }, []);

  const applyProgress = (inv) => {
    if (inv?.status === "active") setStep(4);
    else if (inv?.manifest_status === "signed") setStep(4);
    else if (inv?.csd_status === "valid") setStep(3);
    else if (inv?.profile_complete) setStep(2);
  };

  const loadProfile = useCallback(async (bid) => {
    if (!bid) return;
    try {
      const { data } = await api.get(`/businesses/${bid}/tax-profile`, { withCredentials: true });
      setTp(data.tax_profile);
      setInvoicing(data.invoicing);
      applyProgress(data.invoicing);
    } catch (issue) { setError(issue.response?.data?.detail || "No pudimos cargar el perfil."); }
  }, []);

  const reconcile = useCallback(async ({ silent = false } = {}) => {
    if (!businessId) return null;
    if (!silent) setReconciling(true);
    if (!silent) setError("");
    try {
      const { data } = await api.post(`/businesses/${businessId}/facturapi/reconcile`, {}, { withCredentials: true });
      setInvoicing(data.invoicing);
      setReconcileResult(data);
      applyProgress(data.invoicing);
      if (!silent) {
        setNotice(data.matched
          ? `${data.message}${data.legal_name ? ` · ${data.legal_name}` : ""}`
          : data.message);
        setTimeout(() => setNotice(""), 5000);
      }
      return data;
    } catch (issue) {
      const detail = issue.response?.data?.detail;
      if (!silent) {
        setError(typeof detail === "string" ? detail : detail?.detail || detail?.message || "No pudimos sincronizar con Facturapi.");
      }
      return null;
    } finally { if (!silent) setReconciling(false); }
  }, [businessId]);

  useEffect(() => { loadBusinesses(); }, [loadBusinesses]);
  useEffect(() => { if (businessId) loadProfile(businessId); }, [businessId, loadProfile]);
  // Auto-reconcile silently once per session when RFC is set but no organization_id is stored.
  useEffect(() => {
    if (!businessId || !invoicing || autoSyncedRef.current) return;
    if (tp.rfc && !invoicing.organization_id) {
      autoSyncedRef.current = true;
      reconcile({ silent: true });
    }
  }, [businessId, invoicing, tp.rfc, reconcile]);

  const saveProfile = async () => {
    setSaving(true); setError("");
    try {
      const { data } = await api.put(`/businesses/${businessId}/tax-profile`, tp, { withCredentials: true });
      setInvoicing(data.invoicing);
      setStep(2);
    } catch (issue) {
      const detail = issue.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Revisa los datos fiscales.");
    } finally { setSaving(false); }
  };

  const status = invoicing?.status || "not_configured";
  const meta = STATUS_META[status];

  return (
    <section className="profile-section fiscal-setup-tab" data-testid="fiscal-setup-tab">
      <div className="fiscal-setup-head">
        <div>
          <h2>Configuración fiscal</h2>
          <p>Registra el RFC, sube tu CSD (cifrado con AES-128) y firma la Carta Manifiesto para emitir facturas oficiales bajo tu propio RFC.</p>
        </div>
        {active && (
          <div className="billing-active-hint" data-testid="billing-active-business">
            🏢 {active.business_name || "Mi Empresa"} · {tp.rfc || "Sin RFC"}
          </div>
        )}
      </div>

      <div className="fiscal-reconcile-strip" data-testid="fiscal-reconcile-strip">
        <div className="fiscal-reconcile-copy">
          <b>¿Ya tienes cuenta en Facturapi?</b>
          <small>Buscamos tu organización por RFC y sincronizamos certificados + Carta Manifiesto sin duplicar nada.</small>
        </div>
        <button
          type="button"
          className="cash-btn primary"
          onClick={() => reconcile()}
          disabled={reconciling || !businessId || !tp.rfc}
          data-testid="fiscal-reconcile-button"
        >
          <RefreshCw size={13} className={reconciling ? "spin" : ""} />
          {reconciling ? "Sincronizando..." : "Sincronizar con Facturapi"}
        </button>
      </div>

      {reconcileResult?.matched && (
        <div className="fiscal-reconcile-result matched" data-testid="fiscal-reconcile-matched">
          <ShieldCheck size={20} color="#059669" />
          <div>
            <b>Organización encontrada en Facturapi</b>
            <p>
              {reconcileResult.legal_name || tp.rfc} · Certificado: <b>{reconcileResult.has_certificate ? "válido ✓" : "pendiente"}</b> · Carta Manifiesto: <b>{reconcileResult.has_manifest ? "firmada ✓" : "pendiente"}</b>
            </p>
          </div>
        </div>
      )}
      {reconcileResult && reconcileResult.matched === false && (
        <div className="fiscal-reconcile-result empty" data-testid="fiscal-reconcile-empty">
          <Info size={20} color="#4b5563" />
          <div>
            <b>No hay organización previa en Facturapi</b>
            <p>Continúa con el asistente para dar de alta tu RFC y certificados por primera vez.</p>
          </div>
        </div>
      )}

      <ol className="billing-steps fiscal-steps" data-testid="billing-steps">
        {[
          [1, "Datos fiscales"],
          [2, "Certificado (CSD)"],
          [3, "Carta Manifiesto"],
          [4, "Listo para facturar"],
        ].map(([n, label]) => (
          <li key={n} className={step >= n ? "done" : ""} data-testid={`billing-step-${n}`}>
            <span>{step > n ? <CheckCircle2 size={14} /> : n}</span>
            <b>{label}</b>
          </li>
        ))}
      </ol>

      {invoicing && (
        <section className={`billing-status billing-status-${meta.tone}`} data-testid="billing-status-card">
          <div>
            <small>ESTADO DE FACTURACIÓN</small>
            <b>{meta.tone === "ok" ? "🟢 " : meta.tone === "warn" ? "🟡 " : meta.tone === "bad" ? "🔴 " : "⚪ "}{meta.label}</b>
            <em>{meta.desc}</em>
          </div>
          <ul>
            <li className={tp.rfc ? "ok" : ""}>{tp.rfc ? "✓" : "○"} RFC configurado</li>
            <li className={invoicing.csd_status === "valid" ? "ok" : ""}>{invoicing.csd_status === "valid" ? "✓" : "○"} Certificado válido</li>
            <li className={invoicing.taxpayer_confirmed ? "ok" : ""}>{invoicing.taxpayer_confirmed ? "✓" : "○"} Proveedor conectado</li>
          </ul>
        </section>
      )}

      {error && <p className="profile-error" data-testid="billing-error">{error}</p>}
      {notice && <p className="expenses-notice" data-testid="billing-notice">{notice}</p>}

      {step === 1 && (
        <section className="billing-card" data-testid="billing-step1-form">
          <header><h2>Datos fiscales</h2><p>Estos datos aparecen como emisor en cada factura.</p></header>
          <div className="billing-grid">
            <label><span>RFC</span><input value={tp.rfc} onChange={(e) => setTp({ ...tp, rfc: e.target.value.toUpperCase() })} placeholder="XAXX010101000" data-testid="billing-rfc" /></label>
            <label><span>Nombre o Razón Social</span><input value={tp.legal_name} onChange={(e) => setTp({ ...tp, legal_name: e.target.value })} placeholder="Mi Empresa S.A. de C.V." data-testid="billing-legal-name" /></label>
            <label><span>Régimen Fiscal</span>
              <select value={tp.tax_regime} onChange={(e) => setTp({ ...tp, tax_regime: e.target.value })} data-testid="billing-regime">
                <option value="">Selecciona un régimen</option>
                {REGIMES.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
              </select>
            </label>
            <label><span>Código Postal Fiscal</span><input value={tp.fiscal_postal_code} onChange={(e) => setTp({ ...tp, fiscal_postal_code: e.target.value.replace(/\D/g, "").slice(0, 5) })} placeholder="23400" data-testid="billing-cp" /></label>
            <label className="full"><span>Correo electrónico</span><input type="email" value={tp.email} onChange={(e) => setTp({ ...tp, email: e.target.value })} placeholder="tu@correo.com" data-testid="billing-email" /></label>
          </div>
          <div className="billing-actions">
            <button type="button" className="cash-btn primary" onClick={saveProfile} disabled={saving || !businessId} data-testid="billing-step1-continue">
              {saving ? "Guardando..." : !businessId ? "Cargando empresa..." : "Continuar"} <ArrowRight size={14} />
            </button>
          </div>
        </section>
      )}

      {step === 2 && (
        <section className="billing-card" data-testid="billing-step2-csd">
          <header><h2>Conecta tu Certificado de Sello Digital</h2><p>Tu CSD permite emitir facturas electrónicas bajo <b>tu propio RFC</b>. Se cifra con AES-128 antes de guardarse.</p></header>
          <CsdUploader businessId={businessId} invoicing={invoicing} onUploaded={(inv) => { setInvoicing(inv); setError(""); }} onError={setError} />
          <div className="billing-actions">
            <button type="button" className="cash-btn ghost" onClick={() => setStep(1)} data-testid="billing-step2-back">Atrás</button>
            <button type="button" className="cash-btn primary" onClick={() => setStep(3)} data-testid="billing-step2-continue">
              Continuar <ArrowRight size={14} />
            </button>
          </div>
        </section>
      )}

      {step === 3 && (
        <ManifestStep businessId={businessId} onSigned={() => { setStep(4); setError(""); }} onError={setError} />
      )}

      {step === 4 && (
        <section className="billing-card billing-success" data-testid="billing-step4-success">
          <Sparkles size={28} />
          <h2>🎉 ¡Tu facturación está lista!</h2>
          <p>Todo está configurado correctamente. Ya puedes empezar a crear y emitir facturas bajo tu propio RFC.</p>
          <div className="billing-actions" style={{ justifyContent: "center" }}>
            <button type="button" className="cash-btn primary" onClick={() => navigate("/facturacion")} data-testid="billing-goto-first-invoice">
              <FileSignature size={14} /> Crear mi primera factura
            </button>
            <button type="button" className="cash-btn ghost" onClick={() => navigate("/")} data-testid="billing-goto-dashboard">
              Ir al Dashboard <ArrowRight size={14} />
            </button>
          </div>
        </section>
      )}

      <p className="billing-legal-note">
        <Info size={13} /> Mi Contador es una plataforma tecnológica que facilita la administración y generación de comprobantes fiscales utilizando la información fiscal configurada por cada usuario. Las facturas se emiten bajo tu propio RFC, nunca bajo el RFC de Mi Contador.
      </p>
    </section>
  );
}
